import type {
  ChatContact,
  ChatContactWithChat,
  ChatMember,
  ChatMessage,
  ChatOut,
  DialogChatOut,
  EventChatOut,
  GroupChatOut,
  LastMessage,
  ReplyTo,
  User,
} from '../types'
import { CHAT_TYPES, isGroupLikeChatType, resolveChatAvatarFromIChat, resolveChatDisplayTitle, resolveChatIcon } from '../constants'
import { extractUserId, normalizeAvatarUrl } from '../utils/userDataExtractor'
import { resolveChatOnlineStatus } from '@/modules/chat-matrix-fast/utils/chatOnlineStatus'
import type { IChat, IEmployee, IMessage } from '@/modules/chat-matrix-fast/types/IChat'

interface ApiReplyTo {
  id?: string | number
  content?: string
  attachments?: IMessage['attachments']
  created_by?: User | unknown
  created_at?: string
}

interface ApiUser {
  id?: string | number
  full_name?: string
  first_name?: string
  last_name?: string
  user_name?: string
  username?: string
  email?: string
  avatar?: string
  photo?: string
  uuid?: string
}

export class ChatTransformer {
  static transformChatToChatOut(
    chat: IChat | Partial<ChatOut>,
    messages?: IMessage[],
  ): ChatOut {
    if (!chat) {
      throw new Error('Cannot transform: chat is null or undefined')
    }

    const baseChat = this.createBaseChat(chat, messages)
    const chatType = (chat as IChat).type || chat.type

    if (chatType === CHAT_TYPES.EVENT) {
      return this.createEventChat(baseChat, chat)
    }

    if (chatType === CHAT_TYPES.SPACE) {
      return this.createSpaceChat(baseChat, chat)
    }

    return this.isGroupChat(chatType)
      ? this.createGroupChat(baseChat, chat)
      : this.createDialogChat(baseChat, chat)
  }

  private static createBaseChat(
    chat: IChat | Partial<ChatOut>,
    messages?: IMessage[],
  ) {
    if (!chat) {
      throw new Error('Cannot create base chat: chat is null or undefined')
    }

    const chatMessages: IMessage[]
      = messages !== undefined
        ? messages
        : ((chat as Partial<ChatOut>).messages as unknown as IMessage[]) || []

    const transformedMessages = this.transformMessages(chatMessages)

    // Groups may have last_message without loaded timeline messages
    const originalLastMessage = (chat as IChat).last_message

    const lastMessage = this.getLastMessage(
      { last_message: originalLastMessage },
      transformedMessages,
    )

    const iChat = chat as IChat
    const partial = chat as Partial<ChatOut>

    return {
      id: chat.id || 0,
      unseenMsgs: iChat.unread_count || 0,
      messages: transformedMessages,
      lastMessage,
      is_pinned: Boolean(iChat.is_pinned ?? partial.is_pinned),
      pinned_at: iChat.pinned_at ?? partial.pinned_at ?? null,
    }
  }

  private static isGroupChat(type: string | undefined): boolean {
    return isGroupLikeChatType(type)
  }

  private static createSpaceChat(
    baseChat: {
      id: number | string
      unseenMsgs: number
      messages: ChatMessage[]
      lastMessage: LastMessage
    },
    chat: IChat | Partial<ChatOut>,
  ): import('../types/ChatMessage').SpaceChatOut {
    const groupData = this.createGroupChat(baseChat, chat)

    return {
      ...groupData,
      type: 'space',
      child_room_ids: (chat as IChat).child_room_ids ?? [],
    }
  }

  private static createEventChat(
    baseChat: {
      id: number | string
      unseenMsgs: number
      messages: ChatMessage[]
      lastMessage: LastMessage
    },
    chat: IChat | Partial<ChatOut>,
  ): EventChatOut {
    const groupData = this.createGroupChat(baseChat, chat)

    return {
      ...groupData,
      type: 'event',
      event_url: (chat as IChat).event_url ?? (chat as Partial<EventChatOut>).event_url ?? null,
    }
  }

  private static createGroupChat(
    baseChat: {
      id: number | string
      unseenMsgs: number
      messages: ChatMessage[]
      lastMessage: LastMessage
    },
    chat: IChat | Partial<ChatOut>,
  ): GroupChatOut {
    const iChat = chat as IChat
    const groupChat = chat as Partial<GroupChatOut>

    const originalCreatedBy = iChat.created_by || groupChat.created_by
    const transformedMembers = this.transformMembers(iChat.members || groupChat.members || [])
    const transformedUser = this.transformUser(originalCreatedBy)

    const icon = resolveChatAvatarFromIChat(iChat) || resolveChatIcon(groupChat.icon)
    const normalizedIcon = normalizeAvatarUrl(icon)

    return {
      ...baseChat,
      type: (iChat.type || groupChat.type) as GroupChatOut['type'],
      members: transformedMembers,
      matrix_room_id: iChat.matrix_room_id ?? (groupChat as Partial<GroupChatOut>).matrix_room_id,
      /** GET /chat/{id}/ may return truncated members; full count comes from /members/ */
      total_members:
        (iChat as any).total_members
        ?? (groupChat as any).total_members,
      title: iChat.title || groupChat.title || '',
      description: iChat.description || groupChat.description,
      icon: normalizedIcon || undefined,
      owner: iChat.owner || groupChat.owner || '',
      parent_space_id: iChat.parent_space_id ?? groupChat.parent_space_id,
      discussion_mode: iChat.discussion_mode ?? groupChat.discussion_mode,
      topics_display_mode: iChat.topics_display_mode ?? groupChat.topics_display_mode,
      topics: iChat.topics?.length ? iChat.topics : groupChat.topics,
      created_at: iChat.created_at || groupChat.created_at || '',
      created_by: transformedUser || {} as User,
    } as GroupChatOut
  }

  private static createDialogChat(
    baseChat: {
      id: number | string
      unseenMsgs: number
      messages: ChatMessage[]
      lastMessage: LastMessage
    },
    chat: IChat | Partial<ChatOut> | string | undefined,
  ): DialogChatOut {
    const iChat = typeof chat === 'object' && chat ? chat as IChat : null
    const chatType = typeof chat === 'string'
      ? chat
      : (iChat?.type || (chat as Partial<ChatOut> | null)?.type)

    return {
      ...baseChat,
      type: (chatType as 'dialog' | 'direct') || 'dialog',
      matrix_room_id: iChat?.matrix_room_id,
      matrix_peer_id: iChat?.matrix_peer_id,
    } as DialogChatOut
  }

  static toUiMessages(messages: IMessage[]): ChatMessage[] {
    return this.transformMessages(messages)
  }

  private static transformMessages(messages: IMessage[]): ChatMessage[] {
    return messages.map(msg => this.transformSingleMessage(msg))
  }

  private static transformSingleMessage(msg: IMessage): ChatMessage {
    const replyData = this.transformReplyData(msg)

    const createdBy = msg.created_by ? {
      id: msg.created_by.id,
      first_name: msg.created_by.first_name,
      last_name: msg.created_by.last_name,
      middle_name: msg.created_by.middle_name,
      full_name: msg.created_by.full_name,
      user_name: msg.created_by.user_name || msg.created_by.username,
      avatar: msg.created_by.avatar,
    } as User : undefined

    const avatar = normalizeAvatarUrl(msg.avatar || msg.created_by?.avatar)

    if (createdBy && createdBy.avatar) {
      createdBy.avatar = normalizeAvatarUrl(createdBy.avatar) || undefined
    }

    return {
      id: msg.id || this.generateMessageId(msg),
      matrix_event_id: msg.matrix_event_id,
      message: msg.content,
      time: msg.created_at,
      senderId: this.extractSenderId(msg),
      feedback: this.createFeedback(msg),
      attachments: msg.attachments || [],
      reactions: this.transformReactions(msg),
      created_by: createdBy,
      avatar: avatar || undefined,
      is_edited: msg.is_edited,
      is_deleted: msg.is_deleted,
      is_system: msg.is_system,
      system_target_event_id: msg.system_target_event_id,
      mention_user_ids: msg.mention_user_ids,
      forward: msg.forward,
      ...replyData,
    }
  }

  private static generateMessageId(msg: IMessage): string {
    const senderId = this.extractSenderId(msg)

    return `${senderId}-${msg.created_at}`
  }

  private static extractSenderId(msg: IMessage): string | number {
    const userId = extractUserId({
      user_id: msg.user_id || msg.author_id,
      user: msg.created_by,
    })

    return userId || 'unknown'
  }

  private static createFeedback(msg: IMessage) {
    const eventId = msg.matrix_event_id ? String(msg.matrix_event_id) : ''
    const isPendingEvent = eventId.startsWith('~')
    const sendFailed = msg.matrix_event_status === 'not_sent'

    return {
      isSent: !sendFailed,
      isDelivered: msg.is_delivered ?? (!isPendingEvent && Boolean(eventId) && !sendFailed),
      isSeen: msg.is_read === true,
    }
  }

  private static transformReactions(msg: IMessage) {
    const raw = msg.reactions || msg.message_reactions || []
    return raw.map(r => ({
      id: r.id,
      user_id: r.user || r.user_id || '',
      reaction_type_id: r.reaction_type || r.reaction_type_id || '',
      matrix_emoji_key: r.matrix_emoji_key,
      user_name: r.user_name,
      avatar: r.avatar,
      created_at: r.created_at,
    }))
  }

  private static transformReplyData(msg: IMessage) {
    const replyTo = msg.reply_to || undefined
    const replyToId = this.extractReplyToId(msg)
    const replyMessageId = msg.reply_message_id || undefined

    return {
      reply_to: replyTo ? this.transformReplyTo(replyTo) : undefined,
      reply_to_id: replyToId,
      reply_message_id: replyMessageId,
    }
  }

  private static extractReplyToId(msg: IMessage): number | string | undefined {
    if (msg.reply_to_id) {
      return msg.reply_to_id
    }

    if (typeof msg.reply_to === 'object' && msg.reply_to?.id) {
      return msg.reply_to.id
    }

    if (typeof msg.reply_to === 'number') {
      return msg.reply_to
    }

    return undefined
  }

  private static transformReplyTo(replyTo: ApiReplyTo | unknown): ReplyTo {
    if (!replyTo || typeof replyTo !== 'object') {
      return {
        id: '',
        content: '',
        created_by: {} as User,
        created_at: '',
      }
    }

    const apiReply = replyTo as ApiReplyTo

    const createdBy = apiReply.created_by && typeof apiReply.created_by === 'object'
      ? (apiReply.created_by as User)
      : ({} as User)

    return {
      id: apiReply.id || '',
      content: apiReply.content || '',
      attachments: apiReply.attachments || [],
      created_by: createdBy,
      created_at: apiReply.created_at || '',
    }
  }

  static messageToLastMessage(message: IMessage): LastMessage {
    return {
      message: message.content || '',
      time: message.created_at || '',
      senderId: message.created_by?.id ?? 0,
      feedback: {
        isSent: true,
        isDelivered: true,
        isSeen: message.is_read === true,
      },
      attachments: message.attachments || [],
    }
  }

  private static getLastMessage(
    chat: { last_message?: IMessage },
    transformedMessages: ChatMessage[],
  ): LastMessage {
    const lastMessage = chat.last_message
    const hasContent = lastMessage?.content && lastMessage.content.trim()
    const hasAttachments = lastMessage?.attachments && Array.isArray(lastMessage.attachments) && lastMessage.attachments.length > 0

    if (lastMessage && (hasContent || hasAttachments)) {
      const lastMsg = lastMessage

      return {
        message: lastMsg.content || '',
        time: lastMsg.created_at || '',
        senderId: Number(lastMsg.created_by?.id || 0),
        feedback: {
          isSent: true,
          isDelivered: true,
          isSeen: lastMsg.is_read === true,
        },
        attachments: lastMsg.attachments || [],
      }
    }

    return (
      transformedMessages[transformedMessages.length - 1] || {
        message: 'Нет сообщений',
        time: '',
        senderId: 0,
        feedback: { isSent: false, isDelivered: false, isSeen: false },
      }
    )
  }

  static transformChatsToContacts(
    chats: IChat[],
    profileUserId?: string | number,
  ): ChatContactWithChat[] {
    return chats.map(chat => {
      const contactName = resolveChatDisplayTitle(chat)

      let avatar = resolveChatAvatarFromIChat(chat) || ''
      let online = resolveChatOnlineStatus(chat)

      if (!avatar && chat.type === CHAT_TYPES.DIALOG && chat.members?.length > 0) {
        const profileUserIdStr = String(profileUserId)

        const member = chat.members.find(
          m => String(m.user || m.user_uuid) !== profileUserIdStr,
        )

        if (member) {
          avatar = member.avatar || member.photo || ''
        }
      }

      const normalizedAvatar = normalizeAvatarUrl(avatar)
      const isDialog = chat.type === CHAT_TYPES.DIALOG || chat.type === CHAT_TYPES.DIRECT
      const peerMxid = isDialog ? chat.matrix_peer_id?.trim() : undefined

      const transformedChat = this.transformChatToChatOut(chat)

      return {
        id: chat.id,
        fullName: contactName,
        role: this.resolveContactRole(chat, profileUserId),
        about: chat.description || '',
        avatar: normalizedAvatar || '',
        online,
        matrixUserId: peerMxid || undefined,
        chat: transformedChat,
      }
    })
  }

  /** Подпись под именем: для диалога — должность, иначе тип чата */
  private static resolveContactRole(
    chat: IChat,
    profileUserId?: string | number,
  ): string {
    if (chat.type === CHAT_TYPES.FAVORITE) {
      return 'Заметки'
    }
    if (chat.type === CHAT_TYPES.GROUP) {
      return 'Группа'
    }
    if (chat.type === CHAT_TYPES.CHANNEL) {
      return 'Канал'
    }
    if (chat.type === CHAT_TYPES.EVENT) {
      return 'Задача'
    }
    if (chat.type === CHAT_TYPES.SPACE) {
      return 'Пространство'
    }

    const fromDialogMember = chat.dialog_member?.position_name?.trim()
    if (fromDialogMember) {
      return fromDialogMember
    }

    if (chat.type === CHAT_TYPES.DIALOG && chat.members?.length && profileUserId != null) {
      const profileUserIdStr = String(profileUserId)
      const peer = chat.members.find(
        m => String(m.user || m.user_uuid) !== profileUserIdStr,
      )
      const peerPosition = (peer as { position_name?: string; user?: { position_name?: string } } | undefined)
        ?.position_name
        || (peer as { user?: { position_name?: string } } | undefined)?.user?.position_name
      if (peerPosition?.trim()) {
        return peerPosition.trim()
      }
    }

    return ''
  }

  static transformEmployeesToContacts(employees: IEmployee[]): ChatContact[] {
    return employees.map(emp => {
      const fullName
        = emp.full_name
        || `${emp.first_name || ''} ${emp.last_name || ''}`.trim()
        || 'Неизвестный'

      const employeeId = emp.id !== undefined && emp.id !== null
        ? String(emp.id)
        : `temp-${fullName.toLowerCase().replace(/\s+/g, '-')}`

      const online = emp.online === true

      return {
        id: employeeId,
        employeeId,
        phoneNumber: emp.phone_number || '',
        matrixUserId: emp.matrix_user_id,
        fullName,
        role: typeof emp.position === 'string' ? emp.position : '',
        about: emp.department?.name || (typeof emp.position === 'string' ? emp.position : '') || '',
        avatar: emp.avatar || '',
        online,
      }
    })
  }

  private static transformMembers(
    members: IChat['members'] | ChatMember[],
  ): ChatMember[] {
    return (members || []).map(m => ({
      id: (m as ChatMember).id,
      user: (m as IChat['members'][0]).user || (m as ChatMember).user,
      user_uuid: (m as IChat['members'][0]).user_uuid || (m as ChatMember).user_uuid,
      user_name:
        (m as IChat['members'][0]).user_name || (m as ChatMember).user_name,
      is_admin: (m as IChat['members'][0]).is_admin || (m as ChatMember).is_admin,
      avatar: (m as ChatMember).avatar,
      full_name: (m as ChatMember).full_name,
      photo: (m as ChatMember).photo,
      joined_at: (m as IChat['members'][0]).joined_at || (m as ChatMember).joined_at,
    }))
  }

  private static transformUser(user: unknown): User | undefined {
    if (!user || typeof user !== 'object') {
      return undefined
    }

    const u = user as ApiUser

    const id = u.id || (u as any).user_id || (u as any).user_uuid || u.uuid

    if (id === undefined || (typeof id !== 'number' && typeof id !== 'string')) {
      return undefined
    }

    return {
      id: id as number | string,
      full_name: u.full_name,
      first_name: u.first_name,
      last_name: u.last_name,
      user_name: u.user_name,
      username: u.username,
      email: u.email,
      avatar: u.avatar || u.photo,
      uuid: u.uuid,
    }
  }
}
